import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Package, 
  MessageSquare, 
  Users, 
  Menu, 
  X, 
  LogOut, 
  User,
  BarChart3,
  Tag,
  Building2,
  Wrench,
  Star
} from 'lucide-react';
import clsx from 'clsx';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    {
      name: 'Dashboard',
      href: '/admin',
      icon: BarChart3,
      current: location.pathname === '/admin'
    },
    {
      name: 'Products',
      href: '/admin/products',
      icon: Package,
      current: location.pathname.startsWith('/admin/products')
    },
    {
      name: 'Part Categories',
      href: '/admin/categories',
      icon: Tag,
      current: location.pathname.startsWith('/admin/categories')
    },
    {
      name: 'Manufacturers',
      href: '/admin/manufacturers',
      icon: Building2,
      current: location.pathname.startsWith('/admin/manufacturers')
    },
    {
      name: 'Equipment Types',
      href: '/admin/equipment-types',
      icon: Wrench,
      current: location.pathname.startsWith('/admin/equipment-types')
    },
    {
      name: 'Brands',
      href: '/admin/brands',
      icon: Star,
      current: location.pathname.startsWith('/admin/brands')
    },
    {
      name: 'Contact Requests',
      href: '/admin/contact-requests',
      icon: MessageSquare,
      current: location.pathname.startsWith('/admin/contact-requests')
    },
    {
      name: 'Admin Users',
      href: '/admin/users',
      icon: Users,
      current: location.pathname.startsWith('/admin/users')
    }
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#F5F1EB]">
      {/* Mobile sidebar */}
      <div className={clsx(
        'fixed inset-0 z-50 lg:hidden',
        sidebarOpen ? 'block' : 'hidden'
      )}>
        <div className="fixed inset-0 bg-secondary/75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-white shadow-2xl rounded-r-2xl">
          <div className="flex h-20 items-center justify-between px-6 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <img src="/logo.svg" alt="COT Logo" className="h-10 w-auto" />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-secondary/60 hover:text-secondary p-2 rounded-xl hover:bg-gray-50 transition-colors duration-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-2 px-4 py-6">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={clsx(
                    'group flex items-center px-4 py-3 text-sm font-medium rounded-2xl transition-all duration-300 font-wix',
                    item.current
                      ? 'bg-primary text-white shadow-lg hover:shadow-xl'
                      : 'text-secondary hover:bg-primary/10 hover:text-primary hover:shadow-lg'
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white shadow-xl border-r border-gray-100 rounded-r-2xl">
          <div className="flex h-20 items-center px-6 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <img src="/logo.svg" alt="COT Logo" className="h-16 w-auto" />
            </div>
          </div>
          <nav className="flex-1 space-y-2 px-4 py-6">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={clsx(
                    'group flex items-center px-4 py-3 text-sm font-medium rounded-2xl transition-all duration-300 font-wix',
                    item.current
                      ? 'bg-primary text-white shadow-lg hover:shadow-xl'
                      : 'text-secondary hover:bg-primary/10 hover:text-primary hover:shadow-lg'
                  )}
                >
                  <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-20 shrink-0 items-center gap-x-4 border-b border-gray-100 bg-[#F5F1EB]/95 backdrop-blur-sm px-6 shadow-sm sm:gap-x-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-secondary lg:hidden hover:bg-primary/10 rounded-xl transition-colors duration-300"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* User menu */}
              <div className="relative">
                <button
                  type="button"
                  className="-m-1.5 flex items-center p-1.5 hover:bg-primary/10 rounded-xl transition-colors duration-300"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <span className="sr-only">Open user menu</span>
                  <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <span className="hidden lg:flex lg:items-center">
                    <span className="ml-4 text-sm font-semibold leading-6 text-secondary font-wix">
                      {user?.username}
                    </span>
                  </span>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 z-10 mt-2.5 w-40 origin-top-right rounded-2xl bg-white py-2 shadow-xl ring-1 ring-gray-900/5">
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center px-4 py-2 text-sm leading-6 text-secondary hover:bg-primary/10 hover:text-primary font-wix transition-colors duration-300 rounded-xl mx-1"
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-8">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
